import React from 'react';
import { DmsSelect, DmsTextField } from 'dmsbasereact';
import { Grid, Typography, Box, Divider, Chip } from '@mui/material';
import { DmsAutocomplete } from '../../DmsAutoComplete/DmsAutoComplete';

export const InvoiceDetailsSection = ({
    isCheckin,
    danfeNumber,
    setDanfeNumber,
    ownerEntityPk,
    setOwnerEntityPk,
    destinationEntityPk,
    setDestinationEntityPk,
    organizationBranches,
    // Check-in specific
    checkinInvoices = [],
    isLoadingCheckinInvoices = false,
    selectedCheckinInvoice,
    handleCheckinInvoiceSelect,
    // Check-out specific
    checkoutDestinations = [],
    checkoutOrigins = [],
    isLoadingDestinations = false,
    isLoadingOrigins = false,
    intl,
    fieldHasError,
    helperText,
}) => {
    // Find origin and destination names for display
    const getEntityName = (pk) => {
        const branch = organizationBranches.find(b => b.pk === pk);
        return branch?.name || '';
    };

    if (isCheckin) {
        // Check-in mode: Invoice select with pre-filled origin/destination
        const invoiceOptions = isLoadingCheckinInvoices
            ? [{ value: '', label: intl.get('Loading...').d('Loading...'), disabled: true }]
            : checkinInvoices.length > 0
                ? [
                    { value: '', label: intl.get('SelectInvoice').d('Select an invoice') },
                    ...checkinInvoices.map(inv => ({
                        value: inv.pk,
                        label: `${inv.danfe_number} - ${inv.owner_entity_name}`
                    }))
                ]
                : [{ value: '', label: intl.get('NoPendingInvoices').d('No pending invoices for your institution'), disabled: true }];

        return (
            <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    {intl.get('InvoiceDetails').d('Invoice Details')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <DmsSelect
                            key={`checkin-invoice-select-${checkinInvoices.length}`}
                            label={intl.get('InvoiceNumber').d('Invoice Number')}
                            value={selectedCheckinInvoice?.pk || ''}
                            onChange={(val) => handleCheckinInvoiceSelect(val)}
                            options={invoiceOptions}
                            fullWidth
                        />
                    </Grid>

                    {selectedCheckinInvoice && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <DmsTextField
                                    name="origin"
                                    label={intl.get('Origin').d('Origin')}
                                    value={selectedCheckinInvoice.owner_entity_name || getEntityName(ownerEntityPk)}
                                    disabled
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DmsTextField
                                    name="destination"
                                    label={intl.get('Destination').d('Destination')}
                                    value={selectedCheckinInvoice.destination_entity_name || getEntityName(destinationEntityPk)}
                                    disabled
                                    fullWidth
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
                <Divider sx={{ mt: 3 }} />
            </Box>
        );
    }

    // Check-out mode: Text input for invoice, origin select (user's institutions or all for Eurofarma), destination select
    const originOptions = checkoutOrigins.map(origin => ({
        value: origin.pk,
        label: origin.name,
    }));

    const destinationOptions = checkoutDestinations.map(dest => ({
        value: dest.pk,
        label: dest.is_buyer
            ? `${dest.name} — ${intl.get('Buyer').d('Buyer').toUpperCase()}`
            : dest.name,
    }));

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {intl.get('InvoiceDetails').d('Invoice Details')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="invoiceNumber"
                        label={intl.get('InvoiceNumber').d('Invoice Number') + (fieldHasError ? " *" : "")}
                        value={danfeNumber}
                        onChange={(e) => setDanfeNumber(e.target.value)}
                        fullWidth
                        placeholder={intl.get('EnterInvoiceNumber').d('Enter invoice number')}
                        error={fieldHasError?.('checkout_invoiceNumber')}
                        helperText={helperText?.('checkout_invoiceNumber')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsAutocomplete
                        label={intl.get('Origin').d('Origin')}
                        value={ownerEntityPk}
                        onChange={(val) => setOwnerEntityPk(val)}
                        options={originOptions}
                        loading={isLoadingOrigins}
                        error={fieldHasError?.('checkout_ownerEntityPk')}
                        helperText={helperText?.('checkout_ownerEntityPk')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsAutocomplete
                        label={intl.get('Destination').d('Destination')}
                        value={destinationEntityPk}
                        onChange={(val) => setDestinationEntityPk(val)}
                        options={destinationOptions}
                        loading={isLoadingDestinations}
                        error={fieldHasError?.('checkout_destinationEntityPk')}
                        helperText={helperText?.('checkout_destinationEntityPk')}
                    />
                    {destinationEntityPk && (
                        <Box sx={{ mt: 0.5 }}>
                            {checkoutDestinations.find(d => d.pk === destinationEntityPk)?.is_buyer && (
                                <Chip
                                    label={intl.get('Buyer').d('Buyer')}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            )}
                            {checkoutDestinations.find(d => d.pk === destinationEntityPk)?.is_same_group && (
                                <Chip
                                    label={intl.get('SameGroup').d('Same Group')}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    )}
                </Grid>
            </Grid>
            <Divider sx={{ mt: 3 }} />
        </Box>
    );
};
